import { useMemo } from 'react'

import {
  type DateRange,
  filterByDateRange,
} from '@/lib/utils/filterByDateRange'
import type { Transaction } from '@/modules/projects/stores/types'
import { parseCalendarDate } from '@/utils'

interface MonthlyBreakdown {
  date: string
  inflow: number
  outflow: number
}

interface CategoryTotal {
  date: string
  inflow: number
  outflow: number
}

interface CategoryBreakdownResult {
  categoryTotals: CategoryTotal[]
  detailCategoryTotals: CategoryTotal[]
  monthlyByCategory: Record<string, MonthlyBreakdown[]>
  monthlyByDetailCategory: Record<string, MonthlyBreakdown[]>
  categories: string[]
  detailCategories: string[]
  dataDateRange: { from: string; to: string } | undefined
}

function groupByMonth(transactions: Transaction[]): MonthlyBreakdown[] {
  const monthMap = new Map<string, { inflow: number; outflow: number }>()

  for (const transaction of transactions) {
    const transactionDate = parseCalendarDate(transaction.date)
    const monthKey = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}-01`

    const entry = monthMap.get(monthKey) || { inflow: 0, outflow: 0 }
    if (transaction.amount > 0) {
      entry.inflow += transaction.amount
    } else {
      entry.outflow += Math.abs(transaction.amount)
    }
    monthMap.set(monthKey, entry)
  }

  return Array.from(monthMap.entries())
    .map(([date, values]) => ({ date, ...values }))
    .sort(
      (first, second) =>
        parseCalendarDate(first.date).getTime() -
        parseCalendarDate(second.date).getTime(),
    )
}

function buildCategoryBreakdown(
  transactions: Transaction[],
  categoryField: 'category' | 'categoryDetail',
): {
  totals: CategoryTotal[]
  monthly: Record<string, MonthlyBreakdown[]>
  categoryNames: string[]
} {
  const grouped = new Map<string, Transaction[]>()

  for (const transaction of transactions) {
    const categoryName = transaction[categoryField]
    if (!categoryName) continue

    const group = grouped.get(categoryName) || []
    group.push(transaction)
    grouped.set(categoryName, group)
  }

  const totals: CategoryTotal[] = []
  const monthly: Record<string, MonthlyBreakdown[]> = {}

  for (const [categoryName, categoryTransactions] of grouped) {
    let inflow = 0
    let outflow = 0

    for (const transaction of categoryTransactions) {
      if (transaction.amount > 0) {
        inflow += transaction.amount
      } else {
        outflow += Math.abs(transaction.amount)
      }
    }

    totals.push({ date: categoryName, inflow, outflow })
    monthly[categoryName] = groupByMonth(categoryTransactions)
  }

  totals.sort(
    (first, second) =>
      second.inflow + second.outflow - (first.inflow + first.outflow),
  )

  const categoryNames = totals.map((total) => total.date)

  return { totals, monthly, categoryNames }
}

export function useCategoryBreakdown(
  transactions: Transaction[],
  dateRange: DateRange = 'all',
): CategoryBreakdownResult {
  return useMemo(() => {
    const filtered = filterByDateRange(transactions, dateRange)

    const categoryResult = buildCategoryBreakdown(filtered, 'category')
    const detailResult = buildCategoryBreakdown(filtered, 'categoryDetail')

    const allDates = filtered.map((transaction) => transaction.date).sort()
    const dataDateRange =
      allDates.length > 0
        ? { from: allDates[0], to: allDates[allDates.length - 1] }
        : undefined

    return {
      categoryTotals: categoryResult.totals,
      detailCategoryTotals: detailResult.totals,
      monthlyByCategory: categoryResult.monthly,
      monthlyByDetailCategory: detailResult.monthly,
      categories: categoryResult.categoryNames,
      detailCategories: detailResult.categoryNames,
      dataDateRange,
    }
  }, [transactions, dateRange])
}

export type { MonthlyBreakdown, CategoryTotal, CategoryBreakdownResult }
